(() => {
  if (customElements.get('nor-pufos-product')) return;

  class NorPufosProduct extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.activeIndex = 0;
      this.transitionToken = 0;
      this.galleryVariantToken = 0;
      this.allSlides = Array.from(this.querySelectorAll('[data-nor-slide]'));
      this.allThumbnails = Array.from(this.querySelectorAll('[data-nor-thumbnail]'));
      this.slides = this.orderedVisibleGalleryItems(this.allSlides);
      this.thumbnails = this.orderedVisibleGalleryItems(this.allThumbnails);
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.currentGalleryKind = this.slides.find((slide) => slide.classList.contains('is-variant-primary'))?.dataset.norMediaKind || 'base';
      this.stage = this.querySelector('[data-nor-stage]');
      this.track = this.querySelector('.nor-pufos__slides');
      this.galleryStatus = this.querySelector('[data-nor-gallery-status]');
      this.variantSelect = this.querySelector('[data-nor-variant-select]');
      this.optionGroups = Array.from(this.querySelectorAll('[data-nor-option-group]'));
      this.quantityInput = this.querySelector('[data-nor-quantity-input]');
      this.price = this.querySelector('[data-nor-price]');
      this.compare = this.querySelector('[data-nor-compare]');
      this.inventory = this.querySelector('[data-nor-inventory]');
      this.inventoryLabel = this.querySelector('[data-nor-inventory-label]');
      this.deliveryNote = this.querySelector('[data-nor-delivery-note]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.addButtonText = this.querySelector('[data-add-to-cart-text]');
      this.variants = this.readVariants();
      this.pointerStart = null;
      this.mobileDrag = null;
      this.galleryScrollFrame = null;
      this.mobileGalleryQuery = window.matchMedia('(max-width: 620px)');
      this.bindGallery();
      this.bindVariants();
      this.bindQuantity();
      this.updateVariant();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.cancelAnimationFrame(this.galleryScrollFrame);
    }

    get signal() {
      return this.abortController.signal;
    }

    readVariants() {
      const source = this.querySelector('[data-nor-variants]');
      if (!source) return [];
      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        return [];
      }
    }

    orderedVisibleGalleryItems(items) {
      return items
        .filter((item) => !item.hidden)
        .sort((first, second) => {
          const primaryDifference = Number(second.classList.contains('is-variant-primary')) -
            Number(first.classList.contains('is-variant-primary'));
          if (primaryDifference) return primaryDifference;
          return Number(first.dataset.norMediaPosition) - Number(second.dataset.norMediaPosition);
        });
    }

    galleryKindForVariant(variant) {
      const values = [variant?.title, ...(variant?.options || [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (values.includes('2 perne')) return 'set-2';
      if (values.includes('3 perne')) return 'set-3';
      return 'base';
    }

    applyGalleryKind(kind) {
      const isPrimaryKind = (item) => kind !== 'base' && item.dataset.norMediaKind === kind;
      const shouldShow = (item) => item.dataset.norMediaKind === 'base' || isPrimaryKind(item);

      this.allSlides.forEach((slide) => {
        slide.hidden = !shouldShow(slide);
        slide.classList.toggle('is-variant-primary', isPrimaryKind(slide));
        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
      });

      this.allThumbnails.forEach((thumbnail) => {
        thumbnail.hidden = !shouldShow(thumbnail);
        thumbnail.classList.toggle('is-variant-primary', isPrimaryKind(thumbnail));
        thumbnail.classList.remove('is-active');
        thumbnail.setAttribute('aria-current', 'false');
      });

      this.slides = this.orderedVisibleGalleryItems(this.allSlides);
      this.thumbnails = this.orderedVisibleGalleryItems(this.allThumbnails);

      this.slides.forEach((slide, index) => {
        slide.dataset.index = String(index);
      });

      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.dataset.index = String(index);
        const alt = thumbnail.dataset.norThumbnailAlt || '';
        thumbnail.setAttribute('aria-label', `Vezi imaginea ${index + 1}: ${alt}`);
      });

      this.currentGalleryKind = kind;
      this.transitionToken += 1;
      window.cancelAnimationFrame(this.galleryScrollFrame);
      this.galleryScrollFrame = null;
      this.activateSlide(0);

      if (this.isMobileGallery()) {
        this.track.scrollLeft = this.slides[0]?.offsetLeft || 0;
      }
    }

    async updateGalleryForVariant(variant) {
      const kind = this.galleryKindForVariant(variant);
      if (kind === this.currentGalleryKind) {
        this.applyGalleryKind(kind);
        return;
      }
      const token = ++this.galleryVariantToken;
      const targetSlide = this.allSlides.find((slide) => slide.dataset.norMediaKind === kind) ||
        this.allSlides.find((slide) => slide.dataset.norMediaKind === 'base');
      const targetImage = targetSlide?.querySelector('img');

      if (targetImage && !targetImage.complete) {
        targetImage.loading = 'eager';
        targetImage.fetchPriority = 'high';
        try {
          await targetImage.decode();
        } catch (error) {
          if (!targetImage.complete) return;
        }
      }

      if (token !== this.galleryVariantToken) return;
      this.applyGalleryKind(kind);
    }

    bindGallery() {
      if (!this.slides.length) return;

      this.querySelector('[data-nor-previous]')?.addEventListener('click', () => this.showSlide(this.activeIndex - 1), {
        signal: this.signal,
      });
      this.querySelector('[data-nor-next]')?.addEventListener('click', () => this.showSlide(this.activeIndex + 1), {
        signal: this.signal,
      });

      this.allThumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener(
          'click',
          () => {
            if (!thumbnail.hidden) this.showSlide(Number(thumbnail.dataset.index));
          },
          { signal: this.signal }
        );
      });

      this.stage?.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.showSlide(this.activeIndex - 1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.showSlide(this.activeIndex + 1);
          } else if (event.key === 'Home') {
            event.preventDefault();
            this.showSlide(0);
          } else if (event.key === 'End') {
            event.preventDefault();
            this.showSlide(this.slides.length - 1);
          }
        },
        { signal: this.signal }
      );

      this.stage?.addEventListener(
        'pointerdown',
        (event) => {
          if (this.isMobileGallery()) {
            if (event.pointerType !== 'mouse' || event.target.closest('button')) return;
            this.mobileDrag = {
              x: event.clientX,
              id: event.pointerId,
              index: this.activeIndex,
              scrollLeft: this.track.scrollLeft,
            };
            this.track.classList.add('is-dragging');
            this.stage.setPointerCapture?.(event.pointerId);
            return;
          }
          this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        },
        { signal: this.signal, passive: true }
      );

      this.stage?.addEventListener(
        'pointermove',
        (event) => {
          if (!this.mobileDrag || this.mobileDrag.id !== event.pointerId) return;
          const deltaX = event.clientX - this.mobileDrag.x;
          this.track.scrollLeft = this.mobileDrag.scrollLeft - deltaX;
          if (Math.abs(deltaX) > 3) event.preventDefault();
        },
        { signal: this.signal, passive: false }
      );

      this.stage?.addEventListener(
        'pointerup',
        (event) => {
          if (this.mobileDrag && this.mobileDrag.id === event.pointerId) {
            const drag = this.mobileDrag;
            const deltaX = event.clientX - drag.x;
            const nearestIndex = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
            const targetIndex = Math.abs(deltaX) >= 42
              ? drag.index + (deltaX < 0 ? 1 : -1)
              : nearestIndex;
            this.mobileDrag = null;
            this.track.classList.remove('is-dragging');
            this.stage.releasePointerCapture?.(event.pointerId);
            this.showSlide(targetIndex);
            return;
          }
          if (this.isMobileGallery()) return;
          if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
          const deltaX = event.clientX - this.pointerStart.x;
          const deltaY = event.clientY - this.pointerStart.y;
          this.pointerStart = null;
          if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
          this.showSlide(this.activeIndex + (deltaX < 0 ? 1 : -1));
        },
        { signal: this.signal, passive: true }
      );

      this.stage?.addEventListener('pointercancel', (event) => {
        if (this.mobileDrag?.id === event.pointerId) {
          this.mobileDrag = null;
          this.track.classList.remove('is-dragging');
          this.showSlide(this.activeIndex);
        }
        this.pointerStart = null;
      }, { signal: this.signal });

      this.track?.addEventListener(
        'scroll',
        () => {
          if (!this.isMobileGallery() || this.galleryScrollFrame) return;
          this.galleryScrollFrame = window.requestAnimationFrame(() => {
            this.galleryScrollFrame = null;
            const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
            if (index !== this.activeIndex) this.activateSlide(index);
          });
        },
        { signal: this.signal, passive: true }
      );

      this.mobileGalleryQuery.addEventListener('change', () => this.syncGalleryLayout(), { signal: this.signal });
      this.syncGalleryLayout();
    }

    isMobileGallery() {
      return this.mobileGalleryQuery.matches && this.track && this.slides.length > 1;
    }

    syncGalleryLayout() {
      if (!this.track || !this.slides.length) return;
      if (this.isMobileGallery()) {
        this.track.scrollLeft = this.slides[this.activeIndex]?.offsetLeft || 0;
      } else {
        this.track.scrollLeft = 0;
        this.activateSlide(this.activeIndex, false);
      }
    }

    activateSlide(requestedIndex, announce = true) {
      if (!this.slides.length) return;
      const index = Math.max(0, Math.min(this.slides.length - 1, requestedIndex));
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === index;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-current', String(isActive));
      });

      this.activeIndex = index;
      if (announce && this.galleryStatus) {
        this.galleryStatus.textContent = this.slides[index]?.querySelector('img')?.alt || '';
      }
    }

    async showSlide(requestedIndex) {
      if (this.slides.length < 2) return;
      const index = (requestedIndex + this.slides.length) % this.slides.length;
      if (index === this.activeIndex && !this.isMobileGallery()) return;
      const token = ++this.transitionToken;
      const nextSlide = this.slides[index];
      const nextImage = nextSlide?.querySelector('img');

      if (nextImage && !nextImage.complete) {
        nextImage.loading = 'eager';
        try {
          await nextImage.decode();
        } catch (error) {
          if (!nextImage.complete) return;
        }
      }

      if (token !== this.transitionToken) return;

      this.activateSlide(index);
      if (this.isMobileGallery()) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({
          left: this.slides[index]?.offsetLeft || 0,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }
    }

    bindVariants() {
      this.optionGroups.forEach((group) => {
        group.addEventListener('change', () => this.updateVariant(), { signal: this.signal });
      });
    }

    currentSelections() {
      return this.optionGroups.map((group) => group.querySelector('[data-nor-option-input]:checked')?.value || '');
    }

    matchingVariant(selections = this.currentSelections()) {
      return this.variants.find((variant) =>
        Array.isArray(variant.options) &&
        variant.options.length === selections.length &&
        variant.options.every((value, index) => value === selections[index])
      );
    }

    updateOptionAvailability() {
      const selected = this.currentSelections();
      this.optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('[data-nor-option-input]').forEach((input) => {
          const candidate = [...selected];
          candidate[groupIndex] = input.value;
          const matches = this.variants.filter((variant) =>
            Array.isArray(variant.options) &&
            variant.options.every((value, index) => !candidate[index] || value === candidate[index])
          );
          const exists = matches.length > 0;
          const available = matches.some((variant) => variant.available);
          const label = input.closest('.nor-pufos__option-value');
          input.disabled = !exists;
          label?.classList.toggle('is-unavailable', !exists);
          label?.classList.toggle('is-sold-out', exists && !available);
        });
      });
    }

    updateVariant() {
      this.updateOptionAvailability();
      const variant = this.optionGroups.length ? this.matchingVariant() : this.variants[0];

      if (!variant || !this.variantSelect) {
        this.renderUnavailable();
        return;
      }

      this.variantSelect.value = String(variant.id);
      const option = this.variantSelect.selectedOptions[0];
      const available = variant.available === true && option?.dataset.available === 'true';

      if (this.price && option?.dataset.price) this.price.textContent = option.dataset.price;
      if (this.compare) {
        this.compare.textContent = option?.dataset.compare || '';
        this.compare.hidden = !option?.dataset.compare;
      }

      this.renderAvailability(available);
      this.updateQuantityRules(option);
      this.updateGalleryForVariant(variant);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    }

    renderAvailability(available) {
      this.addButton?.toggleAttribute('disabled', !available);
      if (this.addButtonText) {
        this.addButtonText.textContent = available ? this.dataset.addLabel : this.dataset.addSoldOutLabel;
      }
      this.inventory?.classList.toggle('is-out', !available);
      if (this.inventoryLabel) {
        this.inventoryLabel.textContent = available ? this.dataset.availableLabel : this.dataset.soldOutLabel;
      }
      if (this.deliveryNote) this.deliveryNote.hidden = !available;
    }

    renderUnavailable() {
      if (this.variantSelect) this.variantSelect.selectedIndex = -1;
      this.addButton?.setAttribute('disabled', '');
      if (this.addButtonText) this.addButtonText.textContent = this.dataset.unavailableLabel;
      this.inventory?.classList.add('is-out');
      if (this.inventoryLabel) this.inventoryLabel.textContent = this.dataset.unavailableLabel;
      if (this.deliveryNote) this.deliveryNote.hidden = true;
    }

    updateQuantityRules(option) {
      if (!this.quantityInput || !option) return;
      const minimum = Math.max(1, Number(option.dataset.min) || 1);
      const increment = Math.max(1, Number(option.dataset.increment) || 1);
      const maximum = Number(option.dataset.max);
      this.quantityInput.min = String(minimum);
      this.quantityInput.step = String(increment);
      if (Number.isFinite(maximum) && maximum > 0) {
        this.quantityInput.max = String(maximum);
      } else {
        this.quantityInput.removeAttribute('max');
      }
      this.quantityInput.value = String(this.clampQuantity(Number(this.quantityInput.value) || minimum));
    }

    bindQuantity() {
      if (!this.quantityInput) return;
      this.querySelectorAll('[data-nor-quantity]').forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const direction = button.dataset.norQuantity === 'minus' ? -1 : 1;
            const step = Number(this.quantityInput.step) || 1;
            const current = Number(this.quantityInput.value) || Number(this.quantityInput.min) || 1;
            this.quantityInput.value = String(this.clampQuantity(current + direction * step));
            this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          },
          { signal: this.signal }
        );
      });

      this.quantityInput.addEventListener(
        'change',
        () => {
          this.quantityInput.value = String(this.clampQuantity(Number(this.quantityInput.value)));
        },
        { signal: this.signal }
      );
    }

    clampQuantity(value) {
      const minimum = Number(this.quantityInput?.min) || 1;
      const maximum = Number(this.quantityInput?.max);
      const safeValue = Number.isFinite(value) ? value : minimum;
      return Number.isFinite(maximum) && maximum > 0
        ? Math.min(maximum, Math.max(minimum, safeValue))
        : Math.max(minimum, safeValue);
    }
  }

  customElements.define('nor-pufos-product', NorPufosProduct);
})();
